/**
 * @fileoverview Os números de quê, nas telas de indicadores: a visão de sempre
 * da tela (a conta ativa, no Dashboard; as unidades, no Multi unidades) ou os
 * departamentos que a pessoa vê (GET /departments/mine, relido a cada leitura
 * do acesso: quem entrou ou saiu de um departamento), pedidos à API com
 * ?departmentIds=… (a soma das contas de cada um). Sem escolha, ou se a
 * escolhida deixou de valer: a visão de sempre, senão todos os departamentos,
 * senão o único.
 */

import { useEffect, useMemo, useState } from "react";
import api from "../../services/api";
import usePermission from "../usePermission";
import usePermissionStore from "../../stores/usePermissionStore";

// A visão de sempre da tela; "all" são todos os departamentos da pessoa.
export const BASE_SCOPE = "base";

const useDepartmentScope = (canSeeBase) => {
  const { hasDepartments } = usePermission();
  // Cada leitura do acesso (no foco, no aviso de mudança, depois de um 403) relê a lista.
  const accessLoadedAt = usePermissionStore((state) => state.loadedAt);
  const [departments, setDepartments] = useState([]);
  const [scope, setScope] = useState(null);
  useEffect(() => {
    let active = true;
    if (!hasDepartments) {
      setDepartments([]);
      return undefined;
    }
    api.get("/departments/mine").then(
      ({ data }) => active && setDepartments(Array.isArray(data) ? data : []),
      () => active && setDepartments([])
    );
    return () => { active = false; };
  }, [hasDepartments, accessLoadedAt]);
  useEffect(() => {
    const valid = (value) =>
      (value === BASE_SCOPE && canSeeBase) ||
      (value === "all" && departments.length > 1) ||
      departments.some((department) => String(department.id) === value);
    if (scope !== null && valid(scope)) return;
    const first = canSeeBase ? BASE_SCOPE : departments.length > 1 ? "all" : departments[0] ? String(departments[0].id) : null;
    if (first !== scope) setScope(first);
  }, [canSeeBase, departments, scope]);
  const departmentIds =
    scope === "all" ? departments.map((department) => department.id).join(",") : scope && scope !== BASE_SCOPE ? scope : undefined;
  const scopeParams = useMemo(() => (departmentIds ? { departmentIds } : {}), [departmentIds]);
  return {
    departments,
    scope,
    setScope,
    canSeeBase,
    departmentIds,
    byDepartment: !!departmentIds,
    scopeParams,
    // Antes de saber o que mostrar, nada é pedido (a visão de sempre daria 403 a quem só vê departamentos).
    ready: scope !== null,
    // Muda a cada troca do que se vê: a resposta de uma escolha anterior não vale.
    scopeKey: departmentIds || scope || "",
  };
};

export default useDepartmentScope;
